import React, { useState, useEffect } from 'react';
import { MessageSquare, Users, Shield } from 'lucide-react';
import { apiGet, apiPatch, apiPost, apiDelete } from '../../../utils/api';
import UserSearchSelect from '../../team-chat/UserSearchSelect';

function PrivacySettings() {
  const [privacy, setPrivacy] = useState({
    zeffert_only_dms: false,
    zeffert_only_group_adds: false,
    require_group_invite_confirmation: false,
    hidden_from_clients: false
  });
  const [isStaff, setIsStaff] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProfile();
    loadBlocks();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await apiGet('/profile');
      const data = await response.json();
      if (response.ok) {
        const role = (data.user?.role || '').toLowerCase();
        const email = (data.user?.email || '').toLowerCase();
        setIsStaff(Boolean(data.user?.admin) || ['admin', 'manager', 'director', 'associate'].includes(role) || email.endsWith('@zeffert.com'));
        setPrivacy({
          zeffert_only_dms: Boolean(data.user?.team_chat_privacy?.zeffert_only_dms),
          zeffert_only_group_adds: Boolean(data.user?.team_chat_privacy?.zeffert_only_group_adds),
          require_group_invite_confirmation: Boolean(data.user?.team_chat_privacy?.require_group_invite_confirmation),
          hidden_from_clients: Boolean(data.user?.team_chat_privacy?.hidden_from_clients)
        });
      }
    } catch (e) {
      console.error('Failed to load profile', e);
    }
  };

  const loadBlocks = async () => {
    try {
      const response = await apiGet('/team_chat/blocks');
      const data = await response.json();
      if (response.ok) setBlocks(data.blocks || []);
    } catch (e) {
      console.error('Failed to load blocks', e);
    }
  };

  const toggle = async (key) => {
    const next = { ...privacy, [key]: !privacy[key] };
    const previous = privacy;
    setPrivacy(next);
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiPatch('/profile', { user: { team_chat_privacy: next } });
      const data = await response.json();
      if (!response.ok) {
        setError(data.errors?.join(', ') || 'Failed to save');
        setPrivacy(previous);
      } else {
        setSuccess('Saved.');
      }
    } catch (e) {
      setError('Network error');
      setPrivacy(previous);
    } finally {
      setSaving(false);
    }
  };

  const handleBlock = async (user) => {
    setError('');
    try {
      const response = await apiPost('/team_chat/blocks', { user_id: user.id });
      const data = await response.json();
      if (response.ok) {
        setBlocks(prev => [...prev, data.block]);
      } else {
        setError(data.errors?.join(', ') || data.error || 'Failed to block');
      }
    } catch (e) {
      setError('Network error');
    }
  };

  const handleUnblock = async (userPublicId) => {
    try {
      const response = await apiDelete(`/team_chat/blocks/${userPublicId}`);
      if (response.ok) {
        setBlocks(prev => prev.filter(b => b.id !== userPublicId));
      }
    } catch (e) {
      setError('Network error');
    }
  };

  const PrivacyToggle = ({ on, onClick, disabled }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        on ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <MessageSquare className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold">Direct messages</h2>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-medium">Only Zeffert staff can DM me</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              When on, only Zeffert staff (admins, managers, directors, associates, and @zeffert.com users) can start a direct message with you.
            </p>
          </div>
          <PrivacyToggle on={privacy.zeffert_only_dms} onClick={() => toggle('zeffert_only_dms')} disabled={saving} />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Users className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold">Group chats</h2>
        </div>

        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex-1">
            <h3 className="font-medium">Only Zeffert staff can add me to groups</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              When on, only Zeffert staff can add you to group chats.
            </p>
          </div>
          <PrivacyToggle on={privacy.zeffert_only_group_adds} onClick={() => toggle('zeffert_only_group_adds')} disabled={saving} />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-medium">Require my approval before I'm added to a group</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              When on, group invites land in your list as pending. You accept or decline before joining.
            </p>
          </div>
          <PrivacyToggle on={privacy.require_group_invite_confirmation} onClick={() => toggle('require_group_invite_confirmation')} disabled={saving} />
        </div>
      </div>

      {isStaff && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Shield className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold">Client visibility</h2>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-medium">Hide me from client users' chat picker</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                When on, client users cannot find or start a chat with you. Other Zeffert staff still see you, and any existing conversations keep working.
              </p>
            </div>
            <PrivacyToggle on={privacy.hidden_from_clients} onClick={() => toggle('hidden_from_clients')} disabled={saving} />
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold">Blocked users</h2>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Blocked users can't start chats with you or add you to groups. Existing conversations are unaffected.
        </p>

        <UserSearchSelect
          selectedUsers={[]}
          context="block"
          onSelect={handleBlock}
          onRemove={() => {}}
          excludeIds={blocks.map(b => b.id)}
        />

        <ul className="mt-4 divide-y divide-gray-200 dark:divide-gray-700">
          {blocks.map(b => (
            <li key={b.id} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                {b.avatar_url ? (
                  <img src={b.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                    {(b.first_name || b.username)?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-medium">{b.first_name} {b.last_name}</div>
                  <div className="text-xs text-gray-500">@{b.username}</div>
                </div>
              </div>
              <button
                onClick={() => handleUnblock(b.id)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Unblock
              </button>
            </li>
          ))}
          {blocks.length === 0 && (
            <li className="py-2 text-sm text-gray-500">No blocked users.</li>
          )}
        </ul>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {success && <p className="text-sm text-green-500">{success}</p>}
    </div>
  );
}

export default PrivacySettings;
